import { supabase } from './supabase';

const WEB_PUSH_PUBLIC_KEY =
  process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY ?? process.env.EXPO_PUBLIC_WEB_PUSH_PUBLIC_KEY ?? '';
const SERVICE_WORKER_PATH = '/service-worker.js';

export interface BrowserPushStatus {
  supported: boolean;
  secureContext: boolean;
  configured: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  endpoint?: string | null;
}

const canUseBrowserPush = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const isSecureForPush = () => {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext || window.location.hostname === 'localhost';
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

const getRegistration = async () => {
  if (!canUseBrowserPush() || !isSecureForPush()) {
    return null;
  }

  await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  return navigator.serviceWorker.ready;
};

const serializeSubscription = (subscription: PushSubscription) => {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh ?? null,
    auth: json.keys?.auth ?? null,
    expiration_time: subscription.expirationTime ?? null,
    user_agent: navigator.userAgent,
    device_label: null as string | null,
  };
};

const upsertSubscription = async (subscription: PushSubscription) => {
  const payload = serializeSubscription(subscription);
  const { error } = await supabase.rpc('upsert_web_push_subscription', {
    p_endpoint: payload.endpoint,
    p_p256dh: payload.p256dh,
    p_auth: payload.auth,
    p_expiration_time: payload.expiration_time,
    p_user_agent: payload.user_agent,
    p_device_label: payload.device_label,
  });

  if (error) {
    throw error;
  }
};

const removeSubscriptionRow = async (endpoint?: string | null) => {
  if (!endpoint) return;

  const { error } = await supabase.rpc('remove_web_push_subscription', {
    p_endpoint: endpoint,
  });
  if (error) {
    throw error;
  }
};

export const registerNotificationServiceWorker = async () => {
  if (!canUseBrowserPush() || !isSecureForPush()) {
    return null;
  }

  return getRegistration();
};

export const browserPushService = {
  isSupported() {
    return canUseBrowserPush();
  },

  isConfigured() {
    return Boolean(WEB_PUSH_PUBLIC_KEY.trim());
  },

  async getStatus(userId?: string | null): Promise<BrowserPushStatus> {
    const supported = canUseBrowserPush();
    const secureContext = isSecureForPush();
    const configured = Boolean(WEB_PUSH_PUBLIC_KEY.trim());

    if (!supported || !secureContext) {
      return {
        supported,
        secureContext,
        configured,
        permission: supported ? Notification.permission : 'unsupported',
        subscribed: false,
        endpoint: null,
      };
    }

    const registration = await getRegistration();
    const subscription = registration ? await registration.pushManager.getSubscription() : null;

    if (subscription && userId) {
      await upsertSubscription(subscription);
    }

    return {
      supported,
      secureContext,
      configured,
      permission: Notification.permission,
      subscribed: Boolean(subscription),
      endpoint: subscription?.endpoint ?? null,
    };
  },

  async subscribe(userId: string) {
    if (!userId) {
      throw new Error('Please sign in before enabling browser notifications.');
    }
    if (!canUseBrowserPush()) {
      throw new Error('This browser does not support web push notifications.');
    }
    if (!isSecureForPush()) {
      throw new Error('Browser push requires HTTPS or localhost.');
    }
    if (!WEB_PUSH_PUBLIC_KEY.trim()) {
      throw new Error('Web push is not configured. Add REACT_APP_WEB_PUSH_PUBLIC_KEY before subscribing.');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted.');
    }

    const registration = await getRegistration();
    if (!registration) {
      throw new Error('Failed to register the notifications service worker.');
    }

    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(WEB_PUSH_PUBLIC_KEY.trim()),
      }));

    await upsertSubscription(subscription);
    return subscription;
  },

  async unsubscribe() {
    const registration = await getRegistration();
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    const unsubscribed = await subscription.unsubscribe();

    if (endpoint) {
      await removeSubscriptionRow(endpoint);
    }

    if (!unsubscribed) {
      // If the browser reports false, remove the row so this browser stops receiving pushes.
      await removeSubscriptionRow(endpoint);
    }
  },
};
